import { fitTemplateToVideo, primaryVideo } from "@wooglobe/vre-editor";

/**
 * Fits a template to several raw video clips, merged back-to-back as
 * separate VRE video elements instead of the usual single clip.
 *
 * The first clip goes through the vendored `fitTemplateToVideo` unchanged —
 * same sizing/fit logic as the single-video path. Each further clip is a
 * shallow clone of the resulting primary video element, placed at a computed
 * absolute `time` (the running total of every earlier clip's duration).
 *
 * This deliberately avoids `time: { after: <name> }` chained between two
 * elements we just created in the same batch: the editor's own document
 * normalizer resolves `after` against a name set that does not reliably
 * include sibling elements added together (observed: a clip chained after
 * another new clip, rather than an element already in the template, silently
 * resets to `time: 0` with a console warning — which stacks every clip past
 * the second on top of each other instead of sequencing them). A concrete
 * number has no such resolution step, so it can't misfire this way.
 */
export function fitTemplateToVideos(template, videos) {
  if (!videos || videos.length === 0) return template;

  const doc = fitTemplateToVideo(template, videos[0]);
  if (videos.length === 1) return doc;

  const primary = primaryVideo(doc);
  if (!primary) return doc;

  const usedIds = new Set(doc.elements.map((el) => el.id));
  const usedNames = new Set(doc.elements.map((el) => el.name));
  let cursor = Number.isFinite(primary.duration) && primary.duration > 0 ? primary.duration : 0;
  const extraElements = [];

  videos.slice(1).forEach((clip, index) => {
    const suffix = index + 2;
    let id = `${primary.id}-clip-${suffix}`;
    while (usedIds.has(id)) id = `${id}-x`;
    usedIds.add(id);
    let name = `${primary.name}-clip-${suffix}`;
    while (usedNames.has(name)) name = `${name}-x`;
    usedNames.add(name);

    const duration = Number.isFinite(clip.duration) && clip.duration > 0 ? clip.duration : primary.duration;

    extraElements.push({
      ...primary,
      id,
      name,
      source: clip.source,
      trim_in: 0,
      duration,
      time: cursor,
    });
    cursor += duration;
  });

  return { ...doc, elements: [...doc.elements, ...extraElements] };
}
